import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  OnDestroy,
} from '@angular/core';
import * as echarts from 'echarts/core';
import { InsightsDataService } from '../../insights-data.service';
import { Subject } from 'rxjs';
import { partialIntervalGraph } from '../../insights-data.interface';

@Component({
  selector: 'app-partial-interval-graph',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './partial-interval-graph.component.html',
  styleUrl: './partial-interval-graph.component.scss',
})
export class PartialIntervalGraphComponent implements OnDestroy {
  showTrendLine = false;
  chartInstance!: echarts.ECharts;
  chartInstances: echarts.ECharts[] = [];
  weekGraphData: partialIntervalGraph[] | null = null;
  renderedChartsCount = 0;
  allChartsRendered = false;
  renderedChartsCountTotal = 0;
  allChartsRenderedTotal = false;
  private readonly destroy$ = new Subject<void>();
  totalCharts = 0;
  isShow = true;
  maxLength = 15;
  splitRange = 15;
  behaviorId = 0;
  images: string[] = [];
  @Output() chartImage = new EventEmitter<string>();
  @Output() allChartImage = new EventEmitter<string[]>();
  @Output() allChartTotalImage = new EventEmitter<string[]>();
  chartInstanceTotal!: echarts.ECharts;
  chartsInstanceTotal: echarts.ECharts[] = [];
  @Output() chartTotalImage = new EventEmitter<string>();
  @Input() set graphData(partialIntervalData: partialIntervalGraph) {
    this.behaviorId = partialIntervalData.caseBehaviorId ?? 0;
    this.showTrendLine = partialIntervalData.showTrendLine ?? false;
    this.setGraphOptionsDefault(partialIntervalData, false);
    this.setGraphOptionsDefault(partialIntervalData, true);
  }

  constructor(
    private elementRef: ElementRef,
    private insightsService: InsightsDataService
  ) {}

  setGraphOptionsDefault(
    partialIntervalData: partialIntervalGraph,
    showDaysInterval: boolean
  ) {
    if (showDaysInterval) {
      this.images = [];
      this.chartInstances = [];
      this.renderedChartsCount = 0;
      this.totalCharts = 1;
      this.allChartsRendered = false;
      this.weekGraphData = [];
      const weekData = partialIntervalData;
      const allDataNull =
        weekData.interval1Percent?.every(value => value === null) &&
        weekData.interval2Percent?.every(value => value === null) &&
        weekData.interval3Percent?.every(value => value === null) &&
        weekData.partialIntervalAveragePercent.every(value => value === null);

      this.weekGraphData.push(weekData);
      const { partialIntervalGraphOption, partialIntervalTotalGraphOption } =
        this.setGraphOptions(weekData);
      setTimeout(() => {
        const chartDom = document.getElementById(
          `chart-container-partial-${this.behaviorId}-${0}`
        );
        const chartTotalDom = document.getElementById(
          `chart-container-partial-total-${this.behaviorId}-${0}`
        );
        if (chartDom) {
          const chartInstance = echarts.init(chartDom);
          chartInstance.setOption(partialIntervalGraphOption);

          chartInstance.on('finished', () => {
            this.renderedChartsCount++;

            if (this.renderedChartsCount === this.totalCharts) {
              this.allChartsRendered = true;
              this.exportAllGraphs();
            }
          });

          if (!allDataNull) {
            this.chartInstances.push(chartInstance);
          }
        }
        if (chartTotalDom) {
          const chartInstanceTotal = echarts.init(chartTotalDom);
          chartInstanceTotal.setOption(partialIntervalTotalGraphOption);

          chartInstanceTotal.on('finished', () => {
            this.renderedChartsCountTotal++;
            if (this.renderedChartsCountTotal === this.totalCharts) {
              this.allChartsRenderedTotal = true;
              this.exportAllGraphsTotal();
            }
          });
          if (!allDataNull) {
            this.chartsInstanceTotal.push(chartInstanceTotal);
          }
        }
      }, 500);
    } else {
      const { partialIntervalGraphOption, partialIntervalTotalGraphOption } =
        this.setGraphOptions(partialIntervalData);
      const partialIntervalDom =
        this.elementRef.nativeElement.querySelector('#partial-interval');
      const partialIntervalTotalDom =
        this.elementRef.nativeElement.querySelector('#partial-interval-total');
      if (partialIntervalDom) {
        const chartInstance = echarts.init(partialIntervalDom);
        this.chartInstance = chartInstance;
        chartInstance.setOption(partialIntervalGraphOption);
        chartInstance.on('finished', () => {
          this.emitChartAsImage();
        });
      }
      if (partialIntervalTotalDom) {
        const chartInstance = echarts.init(partialIntervalTotalDom);
        this.chartInstanceTotal = chartInstance;
        chartInstance.setOption(partialIntervalTotalGraphOption);
        chartInstance.on('finished', () => {
          this.emitChartAsImageTotal();
        });
      }
    }
  }

  setGraphOptions(partialInterval: partialIntervalGraph) {
    const dataZoomConfig = [
      {
        type: 'inside',
        xAxisIndex: 0,
        zoomOnMouseWheel: true,
        moveOnMouseMove: true,
        moveOnMouseWheel: true,
        minValueSpan: null,
        maxValueSpan: null,
        start: 0,
        end: 100,
      },
    ];

    const trendlineInterval1 = this.insightsService.generateTrendlineArray(
      partialInterval.xLabels,
      partialInterval.interval1Percent || []
    );
    const trendlineInterval2 = this.insightsService.generateTrendlineArray(
      partialInterval.xLabels,
      partialInterval.interval2Percent || []
    );
    const trendlineInterval3 = this.insightsService.generateTrendlineArray(
      partialInterval.xLabels,
      partialInterval.interval3Percent || []
    );

    const partialIntervalGraphOption = {
      title: {
        text: 'Partial Interval',
        left: 'center',
      },
      tooltip: {
        trigger: 'item',
      },
      legend: {
        data: [
          'Interval 1',
          'Interval 2',
          'Interval 3',
          'Trendline Interval 1',
          'Trendline Interval 2',
          'Trendline Interval 3',
        ],
        left: 'left',
        bottom: 40,
      },
      grid: {
        left: '3%',
        right: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: partialInterval.xLabels,
      },
      yAxis: {
        type: 'value',
        axisLine: { show: true },
        axisLabel: {
          formatter: '{value}%',
        },
        nameLocation: 'center',
        nameTextStyle: {
          padding: [0, 0, 8, 0],
        },
      },
      series: [
        {
          name: 'Interval 1',
          type: 'line',
          data: partialInterval.interval1Percent,
          symbolSize: 10,
          label: {
            show: true,
            position: 'top',
            color: 'blue',
            fontSize: 12,
            formatter: '{c}%',
          },
          connectNulls: true,
          showSymbol: true,
          showAllSymbol: true,
        },
        {
          name: 'Interval 2',
          type: 'line',
          data: partialInterval.interval2Percent,
          symbolSize: 10,
          label: {
            show: true,
            position: 'bottom',
            color: '#47CD89',
            fontSize: 12,
            formatter: '{c}%',
          },
          connectNulls: true,
          showSymbol: true,
          showAllSymbol: true,
        },
        {
          name: 'Interval 3',
          type: 'line',
          data: partialInterval.interval3Percent,
          symbolSize: 10,
          label: {
            show: true,
            position: 'bottom',
            color: 'orange',
            fontSize: 12,
            formatter: '{c}%',
          },
          connectNulls: true,
          showSymbol: true,
          showAllSymbol: true,
        },
        ...(this.showTrendLine
          ? [
              {
                name: 'Trendline Interval 1',
                type: 'line',
                data: trendlineInterval1,
                symbol: 'none',
                lineStyle: {
                  color: 'blue',
                  width: 2,
                  type: 'dashed',
                },
                emphasis: { disabled: true },
                showSymbol: false,
                connectNulls: true,
                z: 10,
              },
              {
                name: 'Trendline Interval 2',
                type: 'line',
                data: trendlineInterval2,
                symbol: 'none',
                lineStyle: {
                  color: '#47CD89',
                  width: 2,
                  type: 'dashed',
                },
                emphasis: { disabled: true },
                showSymbol: false,
                connectNulls: true,
                z: 10,
              },
              {
                name: 'Trendline Interval 3',
                type: 'line',
                data: trendlineInterval3,
                symbol: 'none',
                lineStyle: {
                  color: 'orange',
                  width: 2,
                  type: 'dashed',
                },
                emphasis: { disabled: true },
                showSymbol: false,
                connectNulls: true,
                z: 10,
              },
            ]
          : []),
      ],
      dataZoom: dataZoomConfig,
    };

    const trendlineAverage = this.insightsService.generateTrendlineArray(
      partialInterval.xLabels,
      partialInterval.partialIntervalAveragePercent
    );

    const partialIntervalTotalGraphOption = {
      title: {
        text: 'Partial Interval Average',
        left: 'center',
      },
      tooltip: {
        trigger: 'item',
      },
      legend: {
        data: ['Partial Interval Average', 'Trendline Average'],
        left: 'left',
        bottom: 40,
      },
      grid: {
        left: '3%',
        right: '3%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        boundaryGap: true,
        data: partialInterval.xLabels,
      },
      yAxis: {
        type: 'value',
        axisLine: { show: true },
        axisLabel: {
          formatter: '{value}%',
        },
        nameLocation: 'center',
        nameTextStyle: {
          padding: [0, 0, 8, 0],
        },
      },
      series: [
        {
          name: 'Partial Interval Average',
          type: 'line',
          data: partialInterval.partialIntervalAveragePercent,
          symbolSize: 10,
          itemStyle: {
            color: '#47CD89',
          },
          label: {
            show: true,
            position: 'top',
            color: '#47CD89',
            fontSize: 12,
            formatter: '{c}%',
          },
          connectNulls: true,
          showSymbol: true,
          showAllSymbol: true,
        },
        ...(this.showTrendLine
          ? [
              {
                name: 'Trendline Average',
                type: 'line',
                data: trendlineAverage,
                symbol: 'none',
                lineStyle: {
                  color: 'red',
                  width: 2,
                  type: 'dashed',
                },
                emphasis: { disabled: true },
                showSymbol: false,
                connectNulls: true,
                z: 10,
              },
            ]
          : []),
      ],
      dataZoom: dataZoomConfig,
    };

    return { partialIntervalGraphOption, partialIntervalTotalGraphOption };
  }
  emitChartAsImage() {
    const chartImageBase64 = this.insightsService.convertImagetoBase64(
      this.chartInstance
    );
    this.chartImage.emit(chartImageBase64);
  }
  emitChartAsImageTotal() {
    const chartImageBase64 = this.insightsService.convertImagetoBase64(
      this.chartInstanceTotal
    );
    this.chartTotalImage.emit(chartImageBase64);
  }

  exportAllGraphs() {
    if (!this.allChartsRendered) {
      return;
    }

    const images: string[] = [];

    this.chartInstances.forEach(chartInstance => {
      const base64Image =
        this.insightsService.convertImagetoBase64(chartInstance);

      images.push(base64Image);
    });
    this.allChartImage.emit(images);
  }
  exportAllGraphsTotal() {
    if (!this.allChartsRenderedTotal) {
      return;
    }

    const images: string[] = [];

    this.chartsInstanceTotal.forEach(chartInstance => {
      const base64Image =
        this.insightsService.convertImagetoBase64(chartInstance);
      images.push(base64Image);
    });
    this.allChartTotalImage.emit(images);
  }

  ngOnDestroy(): void {
    this.images = [];
    this.destroy$.next();
    this.destroy$.complete();
  }
}
